import { memo } from 'react';

import { useContextSelector } from 'use-context-selector';
import {
  AnalyticsCategory,
  AnalyticsPageName,
  RoutePath,
} from '@common/constants';
import { useRoute } from '@core/navigation';
import { useTranslation } from '@wearehelpful/ui-localization';
import { TrackPageView } from '@wearehelpful/ui-stats';
import {
  Button,
  Content,
  Footer,
  Header,
  Page,
  Typography,
} from '@wearehelpful/ui-kit';

import { DetailsContext } from '../Details.context';
import { useNavigateNext } from '../Details.hooks';

import { Body, Image, Placeholder } from './Success.style';
import { RouteParams, RouteSearch } from './Success.types';
import { content } from './Success.utils';

export const Success = memo(() => {
  const [{ search, params }] = useRoute<RouteParams, RouteSearch>();
  const { t } = useTranslation();

  const [, navigate] = useNavigateNext({
    ...search,
    handleSuccess: false,
  });

  const survey = useContextSelector(DetailsContext, ({ survey }) => survey);

  const cta = (() => {
    if (survey?.nextSurvey?.slug || survey?.nextScreen?.url) {
      return t('actions.next');
    }
    return t('actions.done');
  })();

  const closable = !survey?.nextSurvey?.slug && !survey?.nextScreen?.url;

  return (
    <TrackPageView
      category={AnalyticsCategory.SURVEY}
      name={AnalyticsPageName.SurveyDetailsSuccess}
      properties={{ slug: params.slug }}
    >
      <Page>
        <Header>
          {closable && (
            <Header.Actions>
              <Header.Close
                onClick={navigate}
                defaultHref={RoutePath.ROOT_HOME}
              />
            </Header.Actions>
          )}
        </Header>
        <Content padding>
          <Content.Subheader>
            <Typography variant="h2" align="center">
              {survey?.success?.title}
            </Typography>
          </Content.Subheader>
          <Content.Fixed inset>
            <Body>{content(survey?.success?.body, {})}</Body>
            {survey?.success?.image?.url && (
              <Placeholder>
                <Image src={survey?.success?.image?.url} alt="" />
              </Placeholder>
            )}
          </Content.Fixed>
        </Content>
        <Footer>
          <Button href={survey?.nextUrl || undefined} onClick={navigate}>
            {cta}
          </Button>
        </Footer>
      </Page>
    </TrackPageView>
  );
});
